import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { track } from '@/lib/analytics';
import { Check, ArrowRight, Sparkles, Shield, Zap, Map } from 'lucide-react';

/**
 * Landing "Proprietari di terreno" — targeting per Facebook/Google Ads
 * proprietari terrieri che vogliono monetizzare terra inutilizzata.
 * Un solo CTA principale, testimonianze, pochi bullet, alta velocità.
 */
export default function Proprietari() {
  useEffect(() => {
    track('landing_view', { audience: 'proprietari' });
    document.title = 'Il tuo terreno vale più di quanto pensi — AgriNexus';
    const set = (sel, attr, val) => document.querySelector(sel)?.setAttribute(attr, val);
    set('meta[name="description"]', 'content', 'Pubblica il tuo terreno gratis e ricevi richieste da agricoltori verificati. Nessuna commissione. AgriNexus, il marketplace terreni agricoli in Italia.');
  }, []);

  const onCTA = (name) => track('landing_cta', { audience: 'proprietari', cta: name });

  return (
    <div className="min-h-screen">
      {/* HERO */}
      <section className="relative overflow-hidden bg-foreground text-background">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 md:py-28 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-accent mb-4">Per proprietari terrieri</div>
            <h1 className="font-display text-4xl md:text-6xl tracking-tighter leading-[0.95]">
              Il tuo terreno<br/>vale più<br/><span className="text-accent italic">di quanto pensi.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-background/70 max-w-lg">
              Pubblica gratis il tuo terreno agricolo e ricevi richieste concrete da agricoltori, investitori e cooperative in tutta Italia. Nessuna commissione, nessun costo nascosto.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/pubblica" onClick={() => onCTA('publish_hero')} data-testid="landing-cta-publish" className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-accent text-foreground font-medium hover:opacity-90 transition">
                Pubblica ora, è gratis <ArrowRight size={16}/>
              </Link>
              <Link to="/come-funziona" onClick={() => onCTA('how_hero')} className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-background/30 text-background/90 hover:bg-background/10 transition">
                Come funziona
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-xs text-background/60">
              <div className="flex items-center gap-1.5"><Check size={14} className="text-accent"/> 100% gratis</div>
              <div className="flex items-center gap-1.5"><Check size={14} className="text-accent"/> Zero commissioni</div>
              <div className="flex items-center gap-1.5"><Check size={14} className="text-accent"/> Contatti diretti</div>
            </div>
          </div>
          <div className="relative">
            <img src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1200&q=80" alt="" className="rounded-2xl w-full h-[420px] object-cover shadow-2xl"/>
            <div className="absolute -bottom-6 -left-6 rounded-2xl bg-background text-foreground p-5 shadow-2xl border border-border max-w-[240px]">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">In media</div>
              <div className="font-display text-3xl mt-1">3-5 richieste</div>
              <div className="text-xs text-muted-foreground mt-1">nei primi 14 giorni dopo la pubblicazione</div>
            </div>
          </div>
        </div>
      </section>

      {/* 3 STEP */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20">
        <div className="text-xs uppercase tracking-[0.3em] font-bold text-terracotta text-center">3 passi, 5 minuti</div>
        <h2 className="font-display text-3xl md:text-5xl tracking-tighter text-center mt-4">Semplice come dovrebbe essere.</h2>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {[
            { n:'01', I:Sparkles, t:'Descrivi il terreno', d:'Wizard guidato in 6 step. Foto, mappa, colture, caratteristiche. L\'AI ti aiuta a scrivere la descrizione perfetta.' },
            { n:'02', I:Map, t:'Appare sulla mappa', d:'Il terreno viene pubblicato subito con posizione geolocalizzata, condivisibile su WhatsApp e Facebook con un click.' },
            { n:'03', I:Zap, t:'Ricevi contatti', d:'Agricoltori interessati ti contattano direttamente via chat, WhatsApp o telefono. Tu decidi con chi parlare.' },
          ].map(s=>(
            <div key={s.n} className="rounded-2xl border border-border p-8 bg-card hover:-translate-y-1 transition-transform">
              <div className="text-xs font-bold text-muted-foreground">{s.n}</div>
              <s.I size={28} className="mt-4 text-terracotta" strokeWidth={1.6}/>
              <h3 className="font-display text-2xl mt-4 tracking-tight">{s.t}</h3>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BENEFICI + BADGE */}
      <section className="bg-secondary/40 py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-terracotta">Perché AgriNexus</div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tighter mt-4">La piattaforma italiana <span className="italic">che rispetta</span> chi lavora la terra.</h2>
              <ul className="mt-8 space-y-4">
                {[
                  ['Nessuna commissione', 'Trattativa diretta tra te e chi è interessato. AgriNexus non prende nulla.'],
                  ['Privacy tutelata', 'Le coordinate esatte non sono pubbliche: solo tu decidi a chi mostrarle.'],
                  ['Analisi AI del terreno', 'Il tuo annuncio include automaticamente un report con clima, altitudine, potenziale colturale.'],
                  ['Contatto diretto', 'WhatsApp, telefono, chat interna. Il proprietario sei tu, decidi tu il canale.'],
                ].map(([t,d])=>(
                  <li key={t} className="flex gap-3">
                    <Check size={20} className="text-accent shrink-0 mt-0.5" strokeWidth={2.4}/>
                    <div>
                      <div className="font-medium">{t}</div>
                      <div className="text-sm text-muted-foreground mt-0.5">{d}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1500595046743-cd271d694d30?auto=format&fit=crop&w=1200&q=80" alt="" className="rounded-2xl w-full h-[500px] object-cover shadow-xl"/>
              <div className="absolute top-6 right-6 rounded-full bg-foreground text-background px-4 py-2 text-xs font-medium flex items-center gap-2">
                <Shield size={14} className="text-accent"/> Verificato GDPR
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-24 text-center">
        <h2 className="font-display text-4xl md:text-6xl tracking-tighter">
          Il tuo prossimo <span className="italic text-terracotta">contatto</span><br/>è a portata di click.
        </h2>
        <p className="mt-6 text-muted-foreground max-w-xl mx-auto">La pubblicazione richiede 5 minuti. Non chiediamo carta di credito, non facciamo abbonamenti nascosti. Beta gratuita in corso.</p>
        <Link to="/pubblica" onClick={() => onCTA('publish_footer')} data-testid="landing-cta-publish-footer" className="mt-10 inline-flex items-center gap-2 h-14 px-9 rounded-full bg-foreground text-background text-base font-medium hover:opacity-90 transition">
          Pubblica il tuo terreno <ArrowRight size={18}/>
        </Link>
      </section>
    </div>
  );
}
